import React, { useEffect, useState } from "react";
import { Plus, Check, X, Banknote } from "lucide-react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Card, Badge, Loading, Modal, fmtDate, fmtMoney, EmptyState } from "../components/ui";

export default function Leave() {
  const { user, meta } = useAuth();
  const [reqs, setReqs] = useState(null);
  const [bal, setBal] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ leaveTypeId: "", startDate: "", endDate: "", reason: "" });
  const [encs, setEncs] = useState([]);
  const [encOpen, setEncOpen] = useState(false);
  const [encForm, setEncForm] = useState({ leaveTypeId: "", days: 1, notes: "" });
  const canApprove = ["SUPERADMIN", "HR_ADMIN", "MANAGER"].includes(user?.role);
  const isHR = ["SUPERADMIN", "HR_ADMIN"].includes(user?.role);

  const load = () => {
    api.get("/leave/requests").then(({ data }) => setReqs(data));
    api.get("/leave/balances").then(({ data }) => setBal(data));
    api.get("/leave/encashments").then(({ data }) => setEncs(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const apply = async (e) => {
    e.preventDefault();
    await api.post("/leave/requests", form);
    setOpen(false); setForm({ leaveTypeId: "", startDate: "", endDate: "", reason: "" }); load();
  };
  const decide = async (id, status) => { await api.put(`/leave/requests/${id}`, { status }); load(); };
  const cancel = async (id) => { if (!window.confirm("Cancel this leave request?")) return; try { await api.post(`/leave/requests/${id}/cancel`); load(); } catch (e) { alert(e.response?.data?.detail || "Failed"); } };
  const applyEnc = async (e) => {
    e.preventDefault();
    try { await api.post("/leave/encashments", { ...encForm, days: +encForm.days }); alert("Encashment requested"); setEncOpen(false); setEncForm({ leaveTypeId: "", days: 1, notes: "" }); load(); }
    catch (err) { alert(apiError(err.response?.data?.detail)); }
  };
  const decideEnc = async (id, status) => { try { await api.put(`/leave/encashments/${id}`, { status }); load(); } catch (e) { alert(apiError(e.response?.data?.detail)); } };

  if (!reqs) return <Loading />;
  return (
    <div>
      <PageHeader title="Leave" subtitle="Apply for and manage leave requests">
        <button data-testid="encash-leave" onClick={() => setEncOpen(true)} className="btn-outline"><Banknote size={16} /> Encash Leave</button>
        <button data-testid="apply-leave" onClick={() => setOpen(true)} className="btn-accent"><Plus size={16} /> Apply Leave</button>
      </PageHeader>

      {bal.length > 0 && (
        <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
          {bal.map((b) => (
            <Card key={b.id} className="p-5" data-testid={`balance-${b.leaveTypeName}`}>
              <p className="lbl">{b.leaveTypeName}</p>
              <div className="mt-2 flex items-end gap-1">
                <span className="font-head text-3xl font-extrabold text-slate-900 dark:text-white">{b.remaining ?? (b.entitled - b.used)}</span>
                <span className="mb-1 text-sm text-slate-400">/ {b.available ?? b.entitled} days left</span>
              </div>
              <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
                <div className="h-full rounded-full bg-accent" style={{ width: `${(b.available ?? b.entitled) ? (b.used / (b.available ?? b.entitled)) * 100 : 0}%` }} />
              </div>
            </Card>
          ))}
        </div>
      )}

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="leave-table">
            <thead><tr><th className="th">Employee</th><th className="th">Type</th><th className="th">From</th><th className="th">To</th><th className="th">Days</th><th className="th">Reason</th><th className="th">Status</th><th className="th">Action</th></tr></thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {reqs.length === 0 && <tr><td colSpan="8"><EmptyState title="No leave requests yet" /></td></tr>}
              {reqs.map((r) => (
                <tr key={r.id} data-testid={`leave-row-${r.id}`}>
                  <td className="td font-medium text-slate-800 dark:text-slate-100">{r.employeeName}</td>
                  <td className="td">{r.leaveTypeName}</td>
                  <td className="td">{fmtDate(r.startDate)}</td>
                  <td className="td">{fmtDate(r.endDate)}</td>
                  <td className="td font-mono">{r.days}</td>
                  <td className="td max-w-[200px] truncate">{r.reason || "—"}</td>
                  <td className="td"><Badge value={r.status} /></td>
                  <td className="td"><div className="flex gap-1.5">
                    {canApprove && r.status === "PENDING" && <>
                      <button data-testid={`approve-${r.id}`} onClick={() => decide(r.id, "APPROVED")} className="rounded-md bg-emerald-100 p-1.5 text-emerald-600 hover:bg-emerald-200 dark:bg-emerald-500/15"><Check size={14} /></button>
                      <button data-testid={`reject-${r.id}`} onClick={() => decide(r.id, "REJECTED")} className="rounded-md bg-red-100 p-1.5 text-red-600 hover:bg-red-200 dark:bg-red-500/15"><X size={14} /></button>
                    </>}
                    {r.employeeId === user?.employeeId && ["PENDING", "APPROVED"].includes(r.status) && <button data-testid={`cancel-${r.id}`} onClick={() => cancel(r.id)} className="rounded-md bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-200 dark:bg-slate-800">Cancel</button>}
                    {!(canApprove && r.status === "PENDING") && !(r.employeeId === user?.employeeId && ["PENDING", "APPROVED"].includes(r.status)) && <span className="text-xs text-slate-400">—</span>}
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {encs.length > 0 && (
        <Card className="mt-6 overflow-hidden">
          <div className="border-b border-slate-100 p-4 dark:border-slate-800"><h3 className="flex items-center gap-1.5 text-sm font-semibold text-slate-700 dark:text-slate-200"><Banknote size={15} /> Leave Encashments</h3></div>
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="encashments-table">
              <thead><tr><th className="th">Employee</th><th className="th">Type</th><th className="th">Days</th><th className="th">Rate/Day</th><th className="th">Amount</th><th className="th">Status</th><th className="th">Action</th></tr></thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {encs.map((r) => (
                  <tr key={r.id} data-testid={`enc-row-${r.id}`}>
                    <td className="td font-medium text-slate-800 dark:text-slate-100">{r.employeeName}</td>
                    <td className="td">{r.leaveTypeName}</td>
                    <td className="td font-mono">{r.days}</td>
                    <td className="td font-mono">{fmtMoney(r.perDayRate)}</td>
                    <td className="td font-mono font-semibold">{fmtMoney(r.amount)}</td>
                    <td className="td"><Badge value={r.status} tone={r.status === "APPROVED" ? "ok" : r.status === "REJECTED" ? "bad" : "warn"} /></td>
                    <td className="td"><div className="flex gap-1.5">
                      {isHR && r.status === "PENDING" && <>
                        <button data-testid={`enc-approve-${r.id}`} onClick={() => decideEnc(r.id, "APPROVED")} className="rounded-md bg-emerald-100 p-1.5 text-emerald-600 dark:bg-emerald-500/15"><Check size={14} /></button>
                        <button data-testid={`enc-reject-${r.id}`} onClick={() => decideEnc(r.id, "REJECTED")} className="rounded-md bg-red-100 p-1.5 text-red-600 dark:bg-red-500/15"><X size={14} /></button>
                      </>}
                    </div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Modal open={encOpen} onClose={() => setEncOpen(false)} title="Encash Leave"
        footer={<><button onClick={() => setEncOpen(false)} className="btn-outline">Cancel</button><button form="enc-form" className="btn-accent">Request</button></>}>
        <form id="enc-form" onSubmit={applyEnc} className="space-y-4">
          <p className="text-sm text-slate-500">Approved encashments are paid at <b>basic ÷ 30 per day</b> in your next payroll run.</p>
          <div><label className="label">Leave Type *</label>
            <select required data-testid="enc-type" value={encForm.leaveTypeId} onChange={(e) => setEncForm({ ...encForm, leaveTypeId: e.target.value })} className="input">
              <option value="">— Select —</option>
              {meta?.leaveTypes?.map((lt) => <option key={lt.id} value={lt.id}>{lt.name}</option>)}
            </select></div>
          <div><label className="label">Days *</label><input required type="number" min="0.5" step="0.5" data-testid="enc-days" value={encForm.days} onChange={(e) => setEncForm({ ...encForm, days: e.target.value })} className="input" /></div>
          <div><label className="label">Notes</label><input data-testid="enc-notes" value={encForm.notes} onChange={(e) => setEncForm({ ...encForm, notes: e.target.value })} className="input" /></div>
        </form>
      </Modal>

      <Modal open={open} onClose={() => setOpen(false)} title="Apply for Leave"
        footer={<><button onClick={() => setOpen(false)} className="btn-outline">Cancel</button><button type="submit" form="leave-form" className="btn-accent">Submit</button></>}>
        <form id="leave-form" onSubmit={apply} className="space-y-4">
          <div><label className="label">Leave Type *</label>
            <select required data-testid="leave-type" value={form.leaveTypeId} onChange={(e) => setForm({ ...form, leaveTypeId: e.target.value })} className="input">
              <option value="">— Select —</option>
              {meta?.leaveTypes?.map((lt) => <option key={lt.id} value={lt.id}>{lt.name}</option>)}
            </select></div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="label">From *</label><input required type="date" data-testid="leave-from" value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} className="input" /></div>
            <div><label className="label">To *</label><input required type="date" data-testid="leave-to" value={form.endDate} onChange={(e) => setForm({ ...form, endDate: e.target.value })} className="input" /></div>
          </div>
          <div><label className="label">Reason</label><textarea data-testid="leave-reason" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="input h-20 py-2" /></div>
        </form>
      </Modal>
    </div>
  );
}
